import { PatientMergePanel } from "@admin/PatientMergePanel";
import { ReferencePhotosPanel } from "@admin/ReferencePhotosPanel";
import { TracesPanel } from "@admin/traces/TracesPanel";
import { CapturePanel } from "@capture/CapturePanel";
import { ClinicBrandingPanel } from "@clinic/ClinicBrandingPanel";
import { ClinicMapPanel } from "@clinicMap/ClinicMapPanel";
import { ConnectClinicPanel } from "@connector/ConnectClinicPanel";
import { useUser } from "@context/UserContext";
import { DisplaysPanel } from "@displays/DisplaysPanel";
import { EmailAdminSettings } from "@email/EmailAdminSettings";
import { CameraFeedPanel } from "@perception/CameraFeed";
import { PhoneSettingsPanel } from "@phone/PhoneSettingsPanel";
import {
  cn,
  Tab,
  TabList,
  TabPanel,
  Tabs,
} from "@scribenote/brian-design-system";
import {
  Navigate,
  useNavigate,
  useRouter,
  useSearch,
} from "@tanstack/react-router";
import { canAccessAdmin } from "@users/userClient";
import { UsersPanel } from "@users/UsersPanel";
import { RUNNING_BUILD } from "@version/deployedBuild";
import { type ReactNode, useState } from "react";

const ADMIN_TABS = [
  "connect",
  "settings",
  "phone",
  "email",
  "users",
  "capture",
  "displays",
  "map",
  "perception",
  "photos",
  "patients",
  "traces",
] as const;

type SettingsTab = (typeof ADMIN_TABS)[number];

interface SettingsSearch {
  tab?: SettingsTab;
  /** The perception room shown full screen. */
  room?: string;
}

// The deployed commit SHA, baked in by the deploy workflow; package.json's version is a placeholder.
const BUILD_VERSION = (RUNNING_BUILD ?? "dev").slice(0, 7);

const isSettingsTab = (value: unknown): value is SettingsTab =>
  ADMIN_TABS.includes(value as SettingsTab);

export const validateSettingsSearch = (
  search: Record<string, unknown>,
): SettingsSearch => ({
  tab: isSettingsTab(search.tab) ? search.tab : undefined,
  room:
    typeof search.room === "string" && search.room ? search.room : undefined,
});

interface TabPanelArgs {
  active: boolean;
  fullScreenRoom: string | undefined;
  onFullScreenRoom: (roomId: string | undefined) => void;
}

const TAB_LABELS: Record<SettingsTab, string> = {
  connect: "Connect",
  settings: "Settings",
  phone: "Phone",
  email: "Email",
  users: "Users",
  capture: "Capture",
  displays: "Displays",
  map: "Clinic map",
  perception: "Perception",
  photos: "Reference photos",
  patients: "Patients",
  traces: "Traces",
};

/** Thunks, not elements: a panel mounts (and fetches) only once its tab is opened. */
const TAB_PANELS: Record<SettingsTab, (args: TabPanelArgs) => ReactNode> = {
  connect: () => <ConnectClinicPanel />,
  settings: () => <ClinicBrandingPanel />,
  phone: () => <PhoneSettingsPanel />,
  email: () => <EmailAdminSettings />,
  users: () => <UsersPanel />,
  capture: () => <CapturePanel />,
  displays: () => <DisplaysPanel />,
  map: () => <ClinicMapPanel />,
  perception: ({ active, fullScreenRoom, onFullScreenRoom }) => (
    <CameraFeedPanel
      active={active}
      fullScreenRoom={fullScreenRoom}
      onFullScreenRoom={onFullScreenRoom}
    />
  ),
  photos: ({ active }) => <ReferencePhotosPanel active={active} />,
  patients: () => <PatientMergePanel />,
  traces: ({ active }) => <TracesPanel active={active} />,
};

export const AdminShell = () => {
  const { currentUser } = useUser();
  const { tab, room } = useSearch({ from: "/workstation/settings" });
  const navigate = useNavigate();
  const router = useRouter();
  const activeTab = tab ?? "settings";
  // Opened panels stay mounted to keep their state; set during render so first open mounts in one commit.
  const [visited, setVisited] = useState<SettingsTab[]>([activeTab]);
  if (!visited.includes(activeTab)) setVisited([...visited, activeTab]);

  // Leaving goes Back over the entry opening pushed, so history keeps no
  // duplicate; a room opened from a link has no such entry and is replaced.
  const [pushedRoom, setPushedRoom] = useState(false);
  if (pushedRoom && !room) setPushedRoom(false);
  const onFullScreenRoom = (roomId: string | undefined) => {
    if (roomId === undefined && pushedRoom) {
      router.history.back();
      return;
    }
    setPushedRoom(roomId !== undefined);
    void navigate({
      to: "/settings",
      search: { tab: activeTab, room: roomId },
      replace: roomId === undefined,
    });
  };

  if (!currentUser) return null;
  if (!canAccessAdmin(currentUser)) return <Navigate to="/board" />;

  return (
    <div
      aria-label="Clinic settings"
      className="flex min-h-0 min-w-0 flex-1 flex-col"
    >
      <Tabs
        value={activeTab}
        onValueChange={(value: string) =>
          navigate({ to: "/settings", search: { tab: value as SettingsTab } })
        }
        data-testid="admin-panel-tabs"
        data-intercom-target="admin-panel-tabs"
        className="min-h-0 min-w-0 flex-1 rounded-[4px] border border-rule bg-card shadow-sheet"
      >
        <TabList
          aria-label="Settings sections"
          className="min-w-0 shrink-0 px-2 pt-2 md:px-6"
        >
          {ADMIN_TABS.map((value) => (
            <Tab
              key={value}
              value={value}
              data-testid={`admin-panel-tabs-tab-${value}`}
              data-intercom-target={`admin-panel-tabs-tab-${value}`}
            >
              {TAB_LABELS[value]}
            </Tab>
          ))}
        </TabList>

        {ADMIN_TABS.map((value) => (
          <TabPanel
            key={value}
            value={value}
            data-testid={`admin-panel-tabs-panel-${value}`}
            data-intercom-target={`admin-panel-tabs-panel-${value}`}
            className={cn(
              "min-h-0",
              value === activeTab && "flex flex-1 flex-col",
            )}
          >
            <div className="min-h-0 flex-1 overflow-auto px-3 pt-4 pb-8 md:px-9 md:pt-7">
              {visited.includes(value)
                ? TAB_PANELS[value]({
                    active: value === activeTab,
                    fullScreenRoom: room,
                    onFullScreenRoom,
                  })
                : null}
            </div>
          </TabPanel>
        ))}
      </Tabs>

      <footer className="pt-2 text-right font-mono text-fine-print text-placeholder">
        build {BUILD_VERSION}
      </footer>
    </div>
  );
};
